import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const Header = () => {
  const location = useLocation();
  const activeSection = location.pathname.split('/')[1] || 'home';
  const { darkMode, toggleDarkMode } = useTheme();
  const [showDiscordTooltip, setShowDiscordTooltip] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <nav className="fixed w-full z-50 bg-gradient-to-b from-[#294C6F]/95 to-[#2978C8]/95 dark:from-[#1a2a3f]/95 dark:to-[#1a4a7a]/95 shadow-2xl backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between items-center py-5">
          {/* Logo and mobile menu button grouped together on the left */}
          <div className="flex items-center">
            <Link to="/" className="text-white font-bold text-xl">Invictable</Link>
            
            {/* Mobile menu button - positioned right after the logo */}
            <button 
              className="ml-4 md:hidden text-white focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            
            {/* Desktop navigation links */}
            <div className="hidden md:flex ml-8 space-x-6 items-center">
              <Link 
                to="/"
                className={`text-white hover:text-gray-200 transition-colors ${activeSection === 'home' ? 'border-b-2 border-white' : ''}`}
              >
                Home
              </Link>
              <Link 
                to="/portfolio"
                className={`text-white hover:text-gray-200 transition-colors ${activeSection === 'portfolio' ? 'border-b-2 border-white' : ''}`}
              >
                Portfolio
              </Link>
            </div>
          </div>
          
          {/* Social Icons and Dark Mode - Kept on right side */}
          <div className="flex items-center space-x-4">
            {/* Twitter Icon */}
            <a 
              href="https://twitter.com/Invictable" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white hover:text-gray-200 transition-colors duration-300"
              aria-label="Twitter"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
            </a>
            
            {/* Discord Icon */}
            <div 
              className="relative"
              onMouseEnter={() => setShowDiscordTooltip(true)}
              onMouseLeave={() => setShowDiscordTooltip(false)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white hover:text-gray-200 transition-colors duration-300" viewBox="0 0 127.14 96.36" fill="currentColor">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
              </svg>
              {showDiscordTooltip && (
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap z-50">
                  Discord: invic
                </div>
              )}
            </div>
            
            {/* Mail Icon */}
            <a 
              href="mailto:realinvictable@gmail.com" 
              className="text-white hover:text-gray-200 transition-colors duration-300"
              aria-label="Email"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </a>
            
            {/* Dark Mode Toggle */}
            <button 
              onClick={toggleDarkMode}
              className="p-2 rounded-full text-white hover:bg-white/10 transition-colors focus:outline-none"
              aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-blue-400/30 animate-fadeIn">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/"
                className={`text-white hover:text-gray-200 transition-colors px-2 py-2 ${activeSection === 'home' ? 'bg-blue-500/30 rounded' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/portfolio"
                className={`text-white hover:text-gray-200 transition-colors px-2 py-2 ${activeSection === 'portfolio' ? 'bg-blue-500/30 rounded' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Portfolio
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header; 